import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  useTheme
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import axiosConfig, { axiosConfigWithoutToken } from '../../../Resources/axiosConfig';
import { AxiosError, AxiosResponse } from 'axios/index';
import routes from '../../../Resources/routes.json';
import { useNavigate } from 'react-router-dom';
import { TokenContext } from '../../../contexts/tokenContext';
import { ErrorMessage, DiagnosticResponse } from '../../../interfaces/S3Types';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import useIsMobile from '../../../hooks/useIsMobile';

interface TestDiagnoseResult {
  source: string;
  alarm: string;
  explanation?: string;
  causes?: string[];
  nextSteps?: string[];
  message?: string;
}

interface LogProps {
  errorLoadingS3Data: boolean;
  id: number;
  status?: number;
}

function Log(props: LogProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const searchParams = new URLSearchParams(location.search);
  const openModal = searchParams.get('open');

  const [warnings, setWarnings] = useState<ErrorMessage[]>([]);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);
  const [errorButtonPressed, setErrorButtonPressed] = useState(
    openModal === 'error' ? true : false
  );
  const [warningButtonPressed, setWarningButtonPressed] = useState(
    openModal === 'warning' ? true : false
  );
  const [updateCount, setUpdateCount] = useState(0);
  const navigate = useNavigate();
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;
  const intl = useIntl();

  /** "AbnormalGridVoltage" → "Abnormal Grid Voltage" */
  const splitCamelCase = (s: string) =>
    s.replace(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/g, ' ').trim();

  /** Returns a translated alarm display name, falling back to camelCase split. */
  const alarmDisplayName = (description: string) =>
    intl.formatMessage({ id: `alarm_${description}`, defaultMessage: splitCamelCase(description) });

  const [diagnoses, setDiagnoses] = useState<{ description: string; lastSeen: string; response: DiagnosticResponse }[]>([]);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [expandedDiagnoses, setExpandedDiagnoses] = useState<Set<number>>(new Set());

  // demo panel state
  const [demoPanelOpen, setDemoPanelOpen] = useState(false);
  const [demoAlarm, setDemoAlarm] = useState<string>('AbnormalGridVoltage');
  const [demoCustom, setDemoCustom] = useState<string>('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState<TestDiagnoseResult | null>(null);

  useEffect(() => {
    axiosConfig
      .get(`/GetAllErrorsForInstallation?id=${props.id}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setErrors(res.data);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });

    axiosConfig
      .get(`/GetAllWarningsForInstallation?id=${props.id}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setWarnings(res.data);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });
  }, [updateCount]);

  // fetch AI diagnosis for all unique errors/warnings from the last 24 hours
  useEffect(() => {
    // filter to last 24 hours only
    const now = new Date();
    const cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const ignore = new Set(['NoAlarm', '0', '']);
    const all = [...errors, ...warnings]
      .filter(item => {
        if (ignore.has(item.description.trim())) return false;
        const itemDate = new Date(item.date + 'T' + item.time);
        return itemDate >= cutoff;
      })
      .sort((a, b) => (b.date + ' ' + b.time).localeCompare(a.date + ' ' + a.time));

    // deduplicate — keep the most-recent occurrence of each unique description
    const seen = new Set<string>();
    const targets: ErrorMessage[] = [];
    for (const item of all) {
      if (!seen.has(item.description)) {
        seen.add(item.description);
        targets.push(item);
      }
    }

    if (targets.length === 0) {
      setDiagnoses([]);
      return;
    }

    // check if the targets changed compared to what we already have
    const currentDescs = diagnoses.map(d => d.description).join('|');
    const newDescs = targets.map(t => t.description).join('|');
    if (currentDescs === newDescs) return;

    setDiagnosisLoading(true);
    Promise.all(
      targets.map(target =>
        axiosConfig
          .get(`/DiagnoseError?installationId=${props.id}&errorDescription=${encodeURIComponent(target.description)}&language=${intl.locale}`)
          .then((res: AxiosResponse<DiagnosticResponse>) => {
            if (res.status === 204 || !res.data || !res.data.explanation) return null;
            return { description: target.description, lastSeen: target.date + ' ' + target.time, response: res.data };
          })
          .catch(() => null)
      )
    ).then(results => {
      setDiagnoses(results.filter(r => r !== null) as { description: string; lastSeen: string; response: DiagnosticResponse }[]);
    }).finally(() => {
      setDiagnosisLoading(false);
    });
  }, [errors, warnings]);

  const handleErrorButtonPressed = () => {
    setErrorButtonPressed(!errorButtonPressed);
  };

  const handleWarningButtonPressed = () => {
    setWarningButtonPressed(!warningButtonPressed);
  };

  const handleErrorAcknowledgeButtonPressed = (errorId: number) => {
    axiosConfig
      .post(`/AcknowledgeError?id=${errorId}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setUpdateCount(updateCount + 1);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });
  };

  const handleWarningAcknowledgeButtonPressed = (warningId: number) => {
    axiosConfig
      .post(`/AcknowledgeWarning?id=${warningId}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setUpdateCount(updateCount + 1);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });
  };

  const DEMO_ALARMS = {
    sinexcel: [
      'AbnormalGridVoltage',
      'GridVoltagePhaseLoss',
      'Battery1NotConnected',
      'Battery1Overvoltage',
      'LithiumBattery1DischargeForbidden',
      'InsulationFault',
      'FanFault',
      'InverterPowerTubeFault',
      'Pv1Overvoltage',
      'IslandProtection',
      'SystemDerating',
      'DcBusOvervoltage',
    ],
    growatt: [
      'StringFault',
      'PvShortCircuited',
      'DcFuseBlown',
      'DcInputVoltageTooHigh',
      'NoUtilityGrid',
      'GridVoltageOutOfRange',
      'FanFailure',
      'BatteryDisconnected',
      'BmsFault',
      'BatteryCommunicationFailure',
      'BatteryVoltageTooHigh',
      'OverTemperatureAlarm',
    ],
  };

  const runDemo = () => {
    const alarm = demoAlarm === '__custom__' ? demoCustom.trim() : demoAlarm;
    if (!alarm) return;
    setDemoLoading(true);
    setDemoResult(null);
    axiosConfigWithoutToken
      .get(`/TestDiagnoseError?errorDescription=${encodeURIComponent(alarm)}&language=${intl.locale}`)
      .then((res: AxiosResponse<TestDiagnoseResult>) => {
        setDemoResult(res.data);
      })
      .catch(() => {
        setDemoResult({ source: 'Error', alarm, message: 'Request failed.' });
      })
      .finally(() => setDemoLoading(false));
  };

  const sourceChip = (source: string) => {
    if (source === 'KnowledgeBase')
      return <Chip label="Knowledge Base" size="small" sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold' }} />;
    if (source === 'MistralAI')
      return <Chip label="AI" size="small" sx={{ bgcolor: '#7b1fa2', color: '#fff', fontWeight: 'bold' }} />;
    if (source === 'MistralFailed')
      return <Chip label="AI failed" size="small" color="error" />;
    return <Chip label="Not available" size="small" color="default" />;
  };


  return (
    <Container maxWidth="xl">
      <Grid container>

        {/* ── AI Diagnosis Demo Panel (commented out — using live AI diagnosis only) ── */}
        {/*
        <Grid item xs={12}>
          <Box sx={{ marginTop: '20px' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setDemoPanelOpen(!demoPanelOpen); setDemoResult(null); }}
              sx={{ textTransform: 'none' }}
            >
              {demoPanelOpen
                ? <FormattedMessage id="demo_hide_button" defaultMessage="Hide AI Diagnosis Demo" />
                : <FormattedMessage id="demo_test_button" defaultMessage="Test AI Diagnosis" />}
            </Button>
          </Box>

          {demoPanelOpen && (
            <Card sx={{ marginTop: '10px', borderLeft: '4px solid #9c27b0', padding: '16px' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                <FormattedMessage id="demo_panel_title" defaultMessage="AI Diagnosis Demo" />
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                <Select
                  size="small"
                  value={demoAlarm}
                  onChange={e => { setDemoAlarm(e.target.value); setDemoResult(null); }}
                  sx={{ minWidth: 260 }}
                >
                  <ListSubheader>inesco 12K - WR Hybrid</ListSubheader>
                  {DEMO_ALARMS.sinexcel.map(a => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                  <ListSubheader>Growatt</ListSubheader>
                  {DEMO_ALARMS.growatt.map(a => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                  <ListSubheader><FormattedMessage id="demo_custom_group" defaultMessage="Custom (may use Mistral AI)" /></ListSubheader>
                  <MenuItem value="__custom__"><FormattedMessage id="demo_custom_option" defaultMessage="Type custom alarm below…" /></MenuItem>
                </Select>

                {demoAlarm === '__custom__' && (
                  <TextField
                    size="small"
                    placeholder={intl.formatMessage({ id: 'demo_custom_placeholder', defaultMessage: 'e.g. UnknownBatteryFault' })}
                    value={demoCustom}
                    onChange={e => { setDemoCustom(e.target.value); setDemoResult(null); }}
                    sx={{ minWidth: 220 }}
                  />
                )}

                <Button
                  variant="contained"
                  size="small"
                  onClick={runDemo}
                  disabled={demoLoading || (demoAlarm === '__custom__' && !demoCustom.trim())}
                  sx={{ textTransform: 'none', bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
                >
                  {demoLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <FormattedMessage id="demo_diagnose_button" defaultMessage="Diagnose" />}
                </Button>
              </Box>

              {demoResult && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>{splitCamelCase(demoResult.alarm)}</strong>
                    </Typography>
                    {sourceChip(demoResult.source)}
                  </Box>

                  {demoResult.message ? (
                    <Typography variant="body2" color="text.secondary">{demoResult.message}</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{demoResult.explanation}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight="bold"><FormattedMessage id="ai_likely_causes" defaultMessage="Likely causes:" /></Typography>
                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                          {(demoResult.causes ?? []).map((c, i) => (
                            <li key={i}><Typography variant="caption">{c}</Typography></li>
                          ))}
                        </ul>
                        <Typography variant="caption" fontWeight="bold"><FormattedMessage id="ai_next_steps" defaultMessage="Suggested next steps:" /></Typography>
                        <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>
                          {(demoResult.nextSteps ?? []).map((s, i) => (
                            <li key={i}><Typography variant="caption">{s}</Typography></li>
                          ))}
                        </ol>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Card>
          )}
        </Grid>
        */}

        {/* AI Diagnosis banner — shown when loading or diagnoses are available */}
        {diagnosisLoading && (
          <Grid item xs={12} md={12}>
            <Card sx={{ marginTop: '20px', borderLeft: '4px solid #1976d2' }}>
              <Box sx={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="ai_analyzing" defaultMessage="AI is analyzing..." />
                </Typography>
              </Box>
            </Card>
          </Grid>
        )}

        {!diagnosisLoading && diagnoses.map((diag, idx) => {
          const isExpanded = expandedDiagnoses.has(idx);
          return (
            <Grid item xs={12} md={12} key={idx}>
              <Card sx={{ marginTop: idx === 0 ? '20px' : '10px', borderLeft: '4px solid #1976d2' }}>
                <Box sx={{ padding: '16px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {diag.response.name || alarmDisplayName(diag.description)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <FormattedMessage id="lastSeen" defaultMessage="Last seen" />: {diag.lastSeen}
                    </Typography>
                  </Box>

                  <Typography variant="body2">
                    {diag.response.explanation}
                  </Typography>

                  <Button
                    size="small"
                    onClick={() => {
                      const next = new Set(expandedDiagnoses);
                      if (isExpanded) next.delete(idx); else next.add(idx);
                      setExpandedDiagnoses(next);
                    }}
                    sx={{ textTransform: 'none', p: 0, mt: 1 }}
                  >
                    <FormattedMessage
                      id={isExpanded ? 'ai_show_less' : 'ai_show_details'}
                      defaultMessage={isExpanded ? 'Show less' : 'Show details'}
                    />
                  </Button>

                  {isExpanded && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        <FormattedMessage id="ai_likely_causes" defaultMessage="Likely causes:" />
                      </Typography>
                      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {diag.response.causes.map((cause, i) => (
                          <li key={i}><Typography variant="caption">{cause}</Typography></li>
                        ))}
                      </ul>

                      <Typography variant="caption" fontWeight="bold">
                        <FormattedMessage id="ai_next_steps" defaultMessage="Suggested next steps:" />
                      </Typography>
                      <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {diag.response.nextSteps.map((step, i) => (
                          <li key={i}><Typography variant="caption">{step}</Typography></li>
                        ))}
                      </ol>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}

        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            onClick={handleErrorButtonPressed}
            sx={{ marginTop: '20px', backgroundColor: errorButtonPressed ? '#808080' : 'default', textTransform: 'none' }}
          >
            <FormattedMessage id="Show Errors" defaultMessage="Show Errors" />
          </Button>

          {errorButtonPressed &&
            errors.length > 0 &&
            (isMobile ? (
              <Box sx={{ marginTop: '10px' }}>
                {errors.map((error, index) => (
                  <Card key={index} sx={{ p: 2, mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 0
                        }}
                      >
                        <ErrorIcon
                          sx={{ color: 'red', width: 25, height: 25 }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="text.primary"
                        >
                          {alarmDisplayName(error.description)}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={error.seen}
                            onChange={() =>
                              error.seen === false &&
                              handleErrorAcknowledgeButtonPressed(error.id)
                            }
                          />
                        }
                        label={
                          <Typography variant="caption">
                            <FormattedMessage id="seen" defaultMessage="Seen" />
                          </Typography>
                        }
                        sx={{ flexShrink: 0, mr: 0 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 1,
                        mt: 1
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="dimgrey"
                          sx={{ display: 'block' }}
                        >
                          <FormattedMessage
                            id="device"
                            defaultMessage="Device"
                          />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {error.deviceCreatedTheMessage}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="dimgrey"
                          sx={{ display: 'block' }}
                        >
                          <FormattedMessage id="date" defaultMessage="Date" />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {error.date}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="dimgrey"
                          sx={{ display: 'block' }}
                        >
                          <FormattedMessage id="time" defaultMessage="Time" />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {error.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
            <Card sx={{ marginTop: '10px' }}>
              <Divider />
              <div>
                <div
                  style={{
                    height: '40px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="type" defaultMessage="Type" />
                    </Typography>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage
                        id="description"
                        defaultMessage="Description"
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="device" defaultMessage="Device" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="date" defaultMessage="Date" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="time" defaultMessage="Time" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="seen" defaultMessage="Seen" />
                    </Typography>
                  </div>
                </div>
                <Divider />
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {errors.map((error, index) => (
                    <>
                      <Divider />
                      <div
                        key={index}
                        style={{
                          height: '40px',
                          marginBottom: '10px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            marginTop: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <ErrorIcon
                            sx={{
                              color: 'red',
                              width: 25,
                              height: 25
                            }}
                          />
                        </div>

                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {alarmDisplayName(error.description)}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {error.deviceCreatedTheMessage}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {error.date}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {error.time}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={error.seen}
                                onChange={() =>
                                  error.seen === false &&
                                  handleErrorAcknowledgeButtonPressed(error.id)
                                }
                                sx={{
                                  marginLeft: '25px'
                                }}
                              />
                            }
                            label=""
                            sx={{ marginTop: 1 }}
                          />
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </Card>
            ))}

          {!props.errorLoadingS3Data &&
            errorButtonPressed &&
            errors.length == 0 && (
              <Alert
                severity="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '20px'
                }}
              >
                <FormattedMessage
                  id="noerrors"
                  defaultMessage="There are no errors"
                />
                <IconButton
                  color="inherit"
                  size="small"
                  sx={{ marginLeft: '4px' }}
                ></IconButton>
              </Alert>
            )}
        </Grid>

        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            onClick={handleWarningButtonPressed}
            sx={{ marginTop: '20px', backgroundColor: warningButtonPressed ? '#808080' : 'default', textTransform: 'none' }}

          >
            <FormattedMessage
              id="Show Warnings"
              defaultMessage="Show Warnings"
            />
          </Button>

          {warningButtonPressed &&
            warnings.length > 0 &&
            (isMobile ? (
              <Box sx={{ marginTop: '10px' }}>
                {warnings.map((warning, index) => (
                  <Card key={index} sx={{ p: 2, mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 0
                        }}
                      >
                        <WarningIcon
                          sx={{ color: 'orange', width: 25, height: 25 }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="text.primary"
                        >
                          {alarmDisplayName(warning.description)}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={warning.seen}
                            onChange={() =>
                              warning.seen === false &&
                              handleWarningAcknowledgeButtonPressed(
                                warning.id
                              )
                            }
                          />
                        }
                        label={
                          <Typography variant="caption">
                            <FormattedMessage id="seen" defaultMessage="Seen" />
                          </Typography>
                        }
                        sx={{ flexShrink: 0, mr: 0 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 1,
                        mt: 1
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="dimgrey"
                          sx={{ display: 'block' }}
                        >
                          <FormattedMessage
                            id="device"
                            defaultMessage="Device"
                          />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {warning.deviceCreatedTheMessage}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="dimgrey"
                          sx={{ display: 'block' }}
                        >
                          <FormattedMessage id="date" defaultMessage="Date" />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {warning.date}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="dimgrey"
                          sx={{ display: 'block' }}
                        >
                          <FormattedMessage id="time" defaultMessage="Time" />
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {warning.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
            <Card sx={{ marginTop: '10px' }}>
              <Divider />
              <div>
                <div
                  style={{
                    height: '40px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="type" defaultMessage="Type" />
                    </Typography>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage
                        id="description"
                        defaultMessage="Description"
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="device" defaultMessage="Device" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="date" defaultMessage="Date" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="time" defaultMessage="Time" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="seen" defaultMessage="Seen" />
                    </Typography>
                  </div>
                </div>
                <Divider />
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {warnings.map((warning, index) => (
                    <>
                      <Divider />
                      <div
                        key={index}
                        style={{
                          height: '40px',
                          marginBottom: '10px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            marginTop: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <WarningIcon
                            sx={{
                              color: 'orange',
                              width: 25,
                              height: 25
                            }}
                          />
                        </div>

                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {alarmDisplayName(warning.description)}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warning.deviceCreatedTheMessage}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warning.date}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warning.time}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={warning.seen}
                                onChange={() =>
                                  warning.seen === false &&
                                  handleWarningAcknowledgeButtonPressed(
                                    warning.id
                                  )
                                }
                                sx={{
                                  marginLeft: '25px'
                                }}
                              />
                            }
                            label=""
                            sx={{ marginTop: 1 }}
                          />
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </Card>
            ))}

          {!props.errorLoadingS3Data &&
            warningButtonPressed &&
            warnings.length == 0 && (
              <Alert
                severity="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  //marginBottom: '20px'
                  marginTop: '20px'
                }}
              >
                <FormattedMessage
                  id="nowarnings"
                  defaultMessage="There are no warnings"
                />
                <IconButton
                  color="inherit"
                  size="small"
                  sx={{ marginLeft: '4px' }}
                ></IconButton>
              </Alert>
            )}
        </Grid>

        <Grid item xs={12} md={12} style={{ marginBottom: '20px' }}>
          {props.errorLoadingS3Data && (
            <Alert
              severity="error"
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px'
              }}
            >
              <FormattedMessage
                id="cannotloadloggingdata"
                defaultMessage="Cannot load logging data"
              />
              <IconButton
                color="inherit"
                size="small"
                sx={{ marginLeft: '4px' }}
              ></IconButton>
            </Alert>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export default Log;
